import { Boundaries } from "@planzone/core";
import {
  id,
  list as listMock,
  project,
  retrieveListError,
  zone,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceList } from "../../common/list";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  listId: Boundaries["retrieveList"]["request"],
): State {
  const stateIndex = state.findIndex(({ id }) => id === listId);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          id: listId,
          request: setRequestStateToPending(),
          list: {
            uuid: id,
            ...listMock,
            zone: { uuid: id, ...zone },
            project,
          },
          error: retrieveListError,
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          id: listId,
          request: setRequestStateToPending(),
          list: {
            uuid: id,
            ...listMock,
            zone: { uuid: id, ...zone },
            project,
          },
          error: retrieveListError,
        },
      ];
}

function handleSuccess(
  state: State,
  list: Boundaries["retrieveList"]["success"],
): State {
  return state.map((item) =>
    item.id === list.uuid
      ? {
          ...item,
          list: {
            ...list,
            parentList: list.parentList
              ? { ...listMock, ...list.parentList }
              : list.parentList,
          },
          request: setRequestStateToSuccess(),
          error: retrieveListError,
        }
      : item,
  );
}

function handleFailure(
  state: State,
  { listId, error }: Boundaries["retrieveList"]["failure"],
): State {
  return state.map((item) =>
    item.id === listId
      ? { ...item, request: setRequestStateToFailure(), error }
      : item,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    list: {
      ...item.list,
      ...reduceList(item.list, event),
      list: item.list.parentList
        ? reduceList(item.list.parentList, event)
        : item.list.parentList,
      project: reduceProject(item.list.project, event),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveListRequested]: handleRequest,
      [events.retrieveListSucceeded]: handleSuccess,
      [events.retrieveListFailed]: handleFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
